<script lang="ts" setup>
import { ref, computed, Ref, watch, onBeforeMount, onBeforeUnmount, inject, getCurrentInstance } from 'vue';
import { useEvents } from '@/plugins/events';
import { useAlerts } from '@/plugins/alerts';
import { KeyValuePair, TermKeyValuePair, Option } from '@/helpers/Interfaces';
import Pager from '@/helpers/Pager';
import { typeName } from '@/helpers/Utils';
import { FormEntry } from '../form';
import { FormBuilderContract } from '@/components/builder/form';
import { AggregateBlueprint } from '@/components/builder/base/blueprints/AggregateBlueprint';
import { LogitoDictionaryEntry, LogitoDictionaryType, instanceOfLogitoDictionaryEntry } from '.';
import DataSourcesService from '@/modules/studio/data-sources/services/DataSourcesService';
import { FormModel as DictionaryTermsFormModel } from '@/modules/core/dictionary-terms/services/DictionaryTermsService';
import AddModal from '@/modules/core/dictionary-terms/modals/AddModal.vue';
import properties from '../../properties';
import DictionaryTermsSelect from './DictionaryTermsSelect.vue';
import { useRoute } from 'vue-router';
import { useControlReload } from '@/components/forms/helpers/RelatedControlsHelper';
import { useDynamicTrigger } from '@/components/forms/helpers/DynamicTriggerHelper';
import { TriggerMoments, TriggerModes } from '@/components/builder/form/traits/HasTriggers';
import { FieldTrigger } from '@/components/forms/properties';
import { registerComponent, unRegisterComponent } from '@/store/triggers';
import { createTriggerComponentCtx, type HasCustomOptions } from '@/components/builder/form/traits/TriggerContext';

defineOptions({
    name: 'logito-dictionary-blueprint',
    components: {
        ...properties,
    },
});

const props = defineProps({
  "blueprint": null,
  "entry": null,
  "form": null,
  "parent": null,
  "index": null,
  "customOptions": null
});

const route = useRoute();
const { $alert } = useAlerts();
const { $events } = useEvents();
const level = inject<number>('level', 0);

const modal = ref<InstanceType<typeof AddModal> | null>(null);
const entryData = ref(new LogitoDictionaryEntry()) as Ref<LogitoDictionaryEntry>;
const pager = ref(new Pager(1, 999, '', 'DESC')) as Ref<Pager>;
const dictionaryPager = ref(new Pager(1, 100, 'Name', 'ASC'));
const sourceOptions = ref<Option[]>([]);
const defaultTerms = ref<KeyValuePair[] | KeyValuePair>([]);
const reloadKey = ref(0);
const isExternalForm = computed(() => route.meta.externalAccess === true);
const customOptions = ref(props.customOptions ?? null);

const triggerComponentCtx = createTriggerComponentCtx(
    {
        customOptions,
        reloadKey
    }
);

const loadControlData = () =>
{
    return props.form.document.initEntry(props.blueprint, entryData.value, instanceOfLogitoDictionaryEntry, props.index);
};

const setControlData = () =>
{
    entryData.value = loadControlData();
};

entryData.value = loadControlData();

onBeforeMount(() =>
{
    if (!props.form.designMode())
    {
        if (props.parent?.type === 'table')
            useControlReload($events, props.blueprint.name, level, props.parent.name, props.index).eventOn(setControlData);
        else
            useControlReload($events, props.blueprint.name, level).eventOn(setControlData);

        registerComponent(props.form.blueprint.id, props.blueprint.name, (getCurrentInstance()?.props), triggerComponentCtx);
    }
});

onBeforeUnmount(() =>
{
    if (!props.form.designMode())
    {
        if (props.parent?.type === 'table')
            useControlReload($events, props.blueprint.name, level, props.parent.name, props.index).eventOff();
        else
            useControlReload($events, props.blueprint.name, level).eventOff();

        unRegisterComponent(props.form.blueprint.id, props.blueprint.name);
    }
});

const blueprint = computed(() => props.blueprint);
const isInTable = computed(() => props.parent?.type === 'table');

const defaultValue = computed({
    get()
    {
        return entryData.value.defaultValue;
    },
    set(value: TermKeyValuePair[])
    {
        entryData.value.defaultValue = value;
    },
});

const value = computed({
    get()
    {
        const { data: value } = entryData.value;

        if (!blueprint.value.multiselect) return value != null ? value[0] : defaultValue.value[0] ?? null;

        return value ?? defaultValue.value ?? null;
    },
    set(value: TermKeyValuePair[] | TermKeyValuePair)
    {
        entryData.value.data = Array.isArray(value) ? value : value ? [value] : [];
        useDynamicTrigger($events, props.form.blueprint.id).emit(TriggerMoments.onChange, props.blueprint, props.parent?.type === 'table' ? props.index : null);
    },
});

const trigger = ref(0);

function updateTrigger()
{
    trigger.value++;
}

const required = computed(() => props.form.expressions.required(props.blueprint, props.index));
const placeholder = computed(() => props.form.localization.translate(props.blueprint.placeholder));
const readonly = computed(() => props.form.expressions.readonly(props.blueprint, false, props.index));
const design = computed(() => props.form.designMode());
const endpoint = computed(() =>
{
    if (design.value) return null;

    if (isExternalForm.value)
    {
        return `external-form/${props.entry.module}/${props.entry.formId}/dictionary-terms/${blueprint.value.name}`;
    }

    if (props.entry.fieldIndex)
    {
        return `modules/${props.entry.moduleId}/table-templates/select-options/${props.entry.fieldIndex}/${blueprint.value.name}`;
    }

    // return simple-dictionary/${props.entry.module}/form/${props.entry.formId}/${props.entry.actionName}/${blueprint.value.name};
    return `${props.entry.module}/${props.entry.formId}/dictionary-terms/${blueprint.value.name}`;
});

const type = computed(() => props.blueprint.dataSource);
const linkedFields = computed(() =>
{
    const result: Record<string, object> = {};

    props.form.schema.components().forEach(p =>
    {
        if (blueprint.value.filterScript?.includes(p.name))
        {
            const entryData = props.entry.find(p.name)?.data;

            result[p.name] = entryData;
        }
    });

    return result;
});

const setValue = (_value: DictionaryTermsFormModel) =>
{
    if (_value)
    {
        const option: TermKeyValuePair = {
            key: _value.publicId,
            value: props.form.localization.translate(_value.name),
            properties: JSON.parse(_value.value ?? '{}'),
        };

        value.value = option;
        reloadKey.value++;
    }
};

const openModal = () =>
{
    modal.value.showModalForDynamicForm(props.entry.module, props.entry.formId, blueprint.value.name);
};

const updateDataSource = (): void =>
{
    defaultTerms.value = [];
    updateDefaultValue();
};

const updateMultiselect = (value: boolean): void =>
{
    if (value)
    {
        if (defaultTerms.value != null && !Array.isArray(defaultTerms.value))
            defaultTerms.value = [defaultTerms.value as KeyValuePair];
        else defaultTerms.value = [];
    }
    else
    {
        if (Array.isArray(defaultTerms.value) && (defaultTerms.value as KeyValuePair[]).length > 0)
            defaultTerms.value = defaultTerms.value[0];
        else defaultTerms.value = null;
    }

    updateDefaultValue();
};

const updateDefaultValue = (): void =>
{
    blueprint.value.defaultValue = Array.isArray(defaultTerms.value)
        ? defaultTerms.value.map((p) => p.key)
        : defaultTerms.value != null
            ? [defaultTerms.value.key]
            : [];
};

const fetchSourceOptions = async (): Promise<void> =>
{
    try
    {
        const response = await DataSourcesService.fetchDynamicControlsDataSource(
            typeName(blueprint.value.type),
            pager.value
        );

        sourceOptions.value = response.items.map((item) => ({ text: item.value, value: item.key }));
    }
    catch (error)
    {
        $alert.danger((error as any).message);
    }
};

const fetchDefaultTerms = async (): Promise<void> =>
{
    if (blueprint.value.defaultValue?.length)
    {
        const result = await DataSourcesService.fetchDefaultTermsList(
            blueprint.value.dataSource,
            new Pager(1, blueprint.value.defaultValue.length, 'Key', 'asc'),
            null,
            `[${blueprint.value.defaultValue.map((p) => `"${p}"`).join(',')}]`
        );

        defaultTerms.value = result.items.map((p) => p.result);

        if (!blueprint.value.multiselect && defaultTerms.value.length > 0)
        {
            defaultTerms.value = defaultTerms.value[0];
        }
    }
};

const fetchDefaultValue = async (): Promise<void> =>
{
    if (blueprint.value.defaultValue?.length)
    {
        const result = await DataSourcesService.fetchDefaultTermsForDocument(
            endpoint.value,
            new Pager(1, 32, '', 'ASC'),
            `[${blueprint.value.defaultValue.map((p) => `"${p}"`).join(',')}]`
        );

        entryData.value.defaultValue = result.items.map((p) => p.result);
    }
};

const init = async (): Promise<void> =>
{
    if (design.value)
    {
        await fetchSourceOptions();
        await fetchDefaultTerms();
    }
    else
    {
        fetchDefaultValue();
    }
};

watch(linkedFields, (oldValue, newValue) =>
{
    if (!isInTable.value)
    {
        reloadKey.value++;
    }
});

init();
</script>

<template>
    <form-component-wrapper class="logitoDictionary-component" :form="form" :parent="parent" :blueprint="blueprint" :table-row-index="props.index">
        <template #default>
            <div class="form-group">
                <form-label :form="form" :blueprint="blueprint" :required="required" :entry="entryData" />
                <div class="d-flex">
                    <dictionary-terms-select
                        v-model="value"
                        :id="blueprint.id"
                        :multiselect="blueprint.multiselect"
                        :placeholder="placeholder"
                        :endpoint="customOptions ? null : endpoint"
                        :options="customOptions"
                        :data-test="blueprint.id"
                        :pager="dictionaryPager"
                        :clear-button="!blueprint.letUsersAddTerms"
                        :custom-classes="{ 'border-radius-right-none': blueprint.letUsersAddTerms }"
                        :reload-key="reloadKey"
                        :deselect="blueprint.letUsersAddTerms"
                        :disabled="readonly"
                        :entry="entry"
                        :index="index"
                        :new-option-select-trigger="trigger"
                        :initial-load="!isInTable"
                        class="flex-fill"
                    />
                    <ideo-button
                        v-if="blueprint.letUsersAddTerms"
                        variant="success"
                        icon="fas fa-plus"
                        class="border-radius-left-none m-0"
                        @click="openModal"
                    />
                </div>
                <form-error-message :entry="entryData" name="value" />
                <form-error-message :entry="entryData" name="custom" />
                <form-help :form="form" :blueprint="blueprint" />
            </div>
            <add-modal ref="modal" :dictionary-type-id="type" :edit-dictionary="false" @reload="setValue" @refresh="updateTrigger" />
        </template>
        <template #properties>
            <field-name :form="form" :blueprint="blueprint" v-model="blueprint.name" />
            <IdeoTabs pills nav-class="nav-justified" content-class="mt-3 docs-container">
                <IdeoTab :title="$t('[[[Konfiguracja]]]')" active>
                    <ideo-form-localize v-slot="{ locale }">
                        <field-text v-model="blueprint.label[locale]" :label="$t('[[[Etykieta]]]')" />
                    </ideo-form-localize>
                    <field-checkbox v-model="blueprint.showLabel" :label="$t('[[[Wyświetlaj etykietę]]]')" />
                    <field-checkbox
                        v-model="blueprint.multiselect"
                        :label="$t('[[[Wielokrotny wybór]]]')"
                        :invalid-feedback="() => form.schema.errorMessage(blueprint, 'multiselect')"
                        @update:modelValue="updateMultiselect"
                    />
                    <field-checkbox
                        v-model="blueprint.letUsersAddTerms"
                        :label="$t('[[[Pozwól dodawać terminy użytkownikom]]]')"
                    />
                    <ideo-form-localize v-slot="{ locale }">
                        <field-text v-model="blueprint.placeholder[locale]" :label="$t('[[[Tekst zastępczy]]]')" />
                    </ideo-form-localize>
                    <field-select
                        v-model="blueprint.dataSource"
                        :label="$t('[[[Źródło danych]]]')"
                        :options="sourceOptions"
                        :invalid-feedback="() => form.schema.errorMessage(blueprint, 'dataSource')"
                        :required="true"
                        @update:modelValue="updateDataSource"
                    />
                    <ideo-form-group v-if="blueprint.dataSource" :label="$t('[[[Wartość domyślna]]]')">
                        <ideo-select
                            v-model="defaultTerms"
                            :endpoint="`admin/forms/default-dictionary-select/${blueprint.dataSource}`"
                            :multiselect="blueprint.multiselect"
                            @update:modelValue="updateDefaultValue"
                        />
                    </ideo-form-group>
                    <ideo-form-localize v-slot="{ locale }">
                        <field-textarea v-model="blueprint.help[locale]" :label="$t('[[[Pomoc]]]')" />
                    </ideo-form-localize>
                    <field-visible :form="form" :blueprint="blueprint" :label="$t('[[[Widoczność]]]')" />
                    <field-readonly :form="form" :blueprint="blueprint" />
                    <field-required :form="form" :blueprint="blueprint" :label="$t('[[[Pole wymagane]]]')" />
                    <field-with-helper v-model="blueprint.filterScript" :label="$t('[[[Skrypt filtrujący]]]')">
                        <template #helper>
                            <div class="legend">
                                <p class="mb-0">
                                    {{
                                        $t(
                                            '[[[Aby zawężyć wybór terminów dostępnych na liście możesz wyfiltrować wpisy na podstawie pól formularza zdefiniowanych w definicji słownika.]]]'
                                        )
                                    }}
                                </p>
                                <p></p>
                                <p class="mb-0">
                                    {{
                                        $t(
                                            '[[[Skrypt musi być napisany w JS i musi definiować obiekt o nazwie filter, który powinien składać się z pól o nazwach zgodnych z kluczami pól formularza i wartościami wg. których nastąpi filtrowanie.]]]'
                                        )
                                    }}
                                </p>
                                <p></p>
                                <p class="mb-8">
                                    {{
                                        $t(
                                            '[[[W skrypcie dostępne są serwisy identycznie jak w skryptach uruchamianych w źródłach danych i na procesie]]]'
                                        )
                                    }}
                                </p>
                                <p class="mb-8">
                                    {{
                                        $t(
                                            '[[[Pod zmienną Entry dostępne są także dane formularza]]]'
                                        )
                                    }}
                                </p>
                                <p>
                                    {{ $t('[[[W kontekście użycia kontrolki w tabeli dynamicznej dostępna jest zmienna: "data.TableRowIndex", która zawiera indeks wiersza tabeli w którym znajduje się kontrolka.]]]') }}
                                </p>
                                <p></p>
                                <p></p>
                                <p>
                                    {{ $t('[[[Przykładowy skrypt wyświetlający terminy, które w polu "unit" mają id działu wybranego w polu UserGroup1]]]') }}
                                </p>
                                <p></p>
                                <div>
                                    <pre>
var unitId = Entry.UserGroup1.Data[0].Key;

var filter = {
    unit: unitId,
};
                            </pre>
                                </div>
                                <p>
                                    {{ $t('[[[Przykładowy skrypt wyświetlający terminy, które w polu "company" mają nazwę spółki z pola Company1]]]') }}
                                </p>
                                <p></p>
                                <div>
                                    <pre>
var companyName = Entry.Company1.Data.Value;

var filter = {
    company: companyName,
};
                            </pre>
                                </div>
                                <p>
                                    {{ $t('[[[Przykładowy skrypt wyświetlający tylko terminy z zaznaczonym checkboxem "exampleBool", gdy użytkownik z pola User1 znajduje się w zespole o id 5]]]') }}
                                </p>
                                <p></p>
                                <div>
                                    <pre>
var filter = {};

var userId = Number.parseInt(Entry.User1.Data[0].Key);
var userData = Users.GetFullUser(userId);
var userInTeam = userData.Teams.Contains(5);

if (userInTeam)
{
    filter["exampleBool"] = true;
}
                            </pre>
                                </div>
                                <p>
                                    {{ $t('[[[Przykładowy skrypt wyświetlający terminy zależne od wbranego terminu w tym samym wierszu tabeli.]]]') }}
                                </p>
                                <p></p>
                                <div>
                                    <pre>
var filter = {};

var miesiacKwartalRok = Entry.Table1.Data[data.TableRowIndex]?.["rodzaj_okresu_rozliczeniowego"]?.Data?.[0].Key;

if (miesiacKwartalRok == "530a1177-0aaf-4a2b-815f-ff1010024eda") {
  filter["isQuarter"] = true;
}
                            </pre>
                                </div>
                                <p>
                                    {{ $t('[[[Przykładowy skrypt, który szuka wartości w polu oznaczonym jako tablica elementów do wyboru. Jeśli wpiszemy kilka wartości, to wszystkie muszą być znalezione w tablicy.]]]') }}
                                </p>
                                <p></p>
                                <div>
                                    <pre>
var filter = {
    accountNumber: ['521']
};
                            </pre>
                                </div>
                            </div>
                        </template>
                    </field-with-helper>
                </IdeoTab>
                <IdeoTab :title="$t('[[[Wyzwalacze]]]')">
                    <FieldTrigger v-model="blueprint.serverSideTriggers" :mode="TriggerModes.ServerSide" :blueprint="blueprint" />
                    <FieldTrigger v-model="blueprint.clientSideTriggers" :mode="TriggerModes.ClientSide" :blueprint="blueprint" />
                </IdeoTab>
            </IdeoTabs>
        </template>
    </form-component-wrapper>
</template>
